import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useSiteSettings } from '../context/SiteSettingsContext';

const SiteCustomization = () => {
  const { makeAuthenticatedRequest } = useAuth();
  const { refreshSettings } = useSiteSettings();
  const [settings, setSettings] = useState({
    site_title: 'Costa Rica Missions 2025-2026',
    site_subtitle: 'Join us in supporting our mission team as they serve communities in Costa Rica.',
    banner_text: '',
    banner_enabled: false,
    primary_color: '#2563eb',
    accent_color: '#10b981',
    background_gradient_from: '#f0fdf4',
    background_gradient_to: '#dbeafe',
    logo_url: '',
    logo_enabled: false,
    footer_text: '',
  });
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [message, setMessage] = useState(null);

  useEffect(() => {
    fetchSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSettings = async () => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await fetch(`${apiUrl}/site-settings`);

      if (response.ok) {
        const data = await response.json();
        setSettings({
          ...data,
          banner_enabled: Boolean(data.banner_enabled),
          logo_enabled: Boolean(data.logo_enabled),
          banner_text: data.banner_text || '',
          logo_url: data.logo_url || '',
          footer_text: data.footer_text || '',
        });
      }
    } catch (error) {
      console.error('Error fetching settings:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setSettings(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setMessage(null);

    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const response = await makeAuthenticatedRequest(`${apiUrl}/site-settings`, {
        method: 'PUT',
        body: JSON.stringify(settings),
      });

      if (response.ok) {
        setMessage({ type: 'success', text: 'Site settings updated successfully! Changes applied.' });
        // Refresh site settings to apply changes immediately
        await refreshSettings();
      } else {
        const error = await response.json();
        setMessage({ type: 'error', text: error.error || 'Failed to update settings' });
      }
    } catch (error) {
      setMessage({ type: 'error', text: 'Network error. Please try again.' });
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="text-center py-12">
        <div className="text-3xl sm:text-4xl mb-4">⚙️</div>
        <p className="text-sm sm:text-base text-gray-600">Loading settings...</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex items-center justify-between border-b pb-3 sm:pb-4">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-800">Site Customization</h2>
        <p className="text-xs sm:text-sm text-gray-500">Customize your site's appearance and content</p>
      </div>

      {message && (
        <div className={`p-3 sm:p-4 rounded-lg ${
          message.type === 'success'
            ? 'bg-green-50 text-green-800 border border-green-200'
            : 'bg-red-50 text-red-800 border border-red-200'
        }`}>
          {message.text}
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Site Information Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Site Information</h3>

          <div className="space-y-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Site Title
              </label>
              <input
                type="text"
                name="site_title"
                value={settings.site_title}
                onChange={handleChange}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Site Subtitle
              </label>
              <textarea
                name="site_subtitle"
                value={settings.site_subtitle}
                onChange={handleChange}
                rows={2}
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Footer Text (Optional)
              </label>
              <textarea
                name="footer_text"
                value={settings.footer_text}
                onChange={handleChange}
                rows={2}
                placeholder="e.g., © 2025 Costa Rica Missions. All rights reserved."
                className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
              />
            </div>
          </div>
        </div>

        {/* Banner Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Announcement Banner</h3>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="banner_enabled"
                checked={settings.banner_enabled}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-xs sm:text-sm text-gray-700">
                Enable banner
              </label>
            </div>

            {settings.banner_enabled && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Banner Text
                </label>
                <input
                  type="text"
                  name="banner_text"
                  value={settings.banner_text}
                  onChange={handleChange}
                  placeholder="e.g., Join us for our info session on March 15th!"
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
              </div>
            )}
          </div>
        </div>

        {/* Colors Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Colors</h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Primary Color (Buttons, Links)
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  name="primary_color"
                  value={settings.primary_color}
                  onChange={handleChange}
                  className="w-16 h-10 sm:h-12 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.primary_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, primary_color: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                  placeholder="#2563eb"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Accent Color (Progress, Success)
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  name="accent_color"
                  value={settings.accent_color}
                  onChange={handleChange}
                  className="w-16 h-10 sm:h-12 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.accent_color}
                  onChange={(e) => setSettings(prev => ({ ...prev, accent_color: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                  placeholder="#10b981"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Background Gradient From
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  name="background_gradient_from"
                  value={settings.background_gradient_from}
                  onChange={handleChange}
                  className="w-16 h-10 sm:h-12 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.background_gradient_from}
                  onChange={(e) => setSettings(prev => ({ ...prev, background_gradient_from: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                  placeholder="#f0fdf4"
                />
              </div>
            </div>

            <div>
              <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                Background Gradient To
              </label>
              <div className="flex gap-2">
                <input
                  type="color"
                  name="background_gradient_to"
                  value={settings.background_gradient_to}
                  onChange={handleChange}
                  className="w-16 h-10 sm:h-12 border border-gray-300 rounded cursor-pointer"
                />
                <input
                  type="text"
                  value={settings.background_gradient_to}
                  onChange={(e) => setSettings(prev => ({ ...prev, background_gradient_to: e.target.value }))}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm sm:text-base"
                  placeholder="#dbeafe"
                />
              </div>
            </div>
          </div>

          {/* Color Preview */}
          <div className="mt-4 p-4 rounded-lg" style={{
            background: `linear-gradient(to right, ${settings.background_gradient_from}, ${settings.background_gradient_to})`
          }}>
            <div className="text-center space-y-2">
              <button
                type="button"
                style={{ backgroundColor: settings.primary_color }}
                className="px-4 py-2 text-white rounded-lg text-sm sm:text-base"
              >
                Primary Button
              </button>
              <div
                style={{ backgroundColor: settings.accent_color }}
                className="h-4 rounded-full mx-auto max-w-xs"
              />
              <p className="text-xs text-gray-600">Background Preview</p>
            </div>
          </div>
        </div>

        {/* Logo Section */}
        <div className="bg-white border border-gray-200 rounded-lg p-4 sm:p-6">
          <h3 className="text-base sm:text-lg font-semibold text-gray-800 mb-3 sm:mb-4">Logo</h3>

          <div className="space-y-4">
            <div className="flex items-center">
              <input
                type="checkbox"
                name="logo_enabled"
                checked={settings.logo_enabled}
                onChange={handleChange}
                className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
              />
              <label className="ml-2 text-xs sm:text-sm text-gray-700">
                Enable custom logo
              </label>
            </div>

            {settings.logo_enabled && (
              <div>
                <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-1 sm:mb-2">
                  Logo URL
                </label>
                <input
                  type="url"
                  name="logo_url"
                  value={settings.logo_url}
                  onChange={handleChange}
                  placeholder="https://example.com/logo.png"
                  className="w-full px-3 py-2 sm:px-4 sm:py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm sm:text-base"
                />
                {settings.logo_url && (
                  <div className="mt-2">
                    <img src={settings.logo_url} alt="Logo preview" className="h-16 object-contain" />
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Save Button */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={isSaving}
            className="bg-blue-600 text-white px-4 sm:px-6 py-2 sm:py-3 rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm sm:text-base"
          >
            {isSaving ? 'Saving...' : 'Save Changes'}
          </button>
        </div>
      </form>

      {/* Info */}
      <div className="bg-blue-50 p-4 sm:p-6 rounded-lg">
        <h3 className="font-semibold text-blue-800 mb-2 text-sm sm:text-base">💡 Customization Tips</h3>
        <ul className="text-xs sm:text-sm text-blue-700 space-y-1">
          <li>• Changes will apply site-wide after saving</li>
          <li>• Use the color picker or enter hex codes manually</li>
          <li>• Logo should be a transparent PNG for best results</li>
          <li>• Refresh the page to see your changes reflected</li>
        </ul>
      </div>
    </div>
  );
};

export default SiteCustomization;
